// components/playground/sections/ScenarioInfoCard.jsx
import React from 'react';

const ScenarioInfoCard = ({
  currentScenario,
  hasUnsavedChanges,
  editMode,
  editedName,
  editedDescription,
  selectedYear,
  averagingMethod,
  adjustmentCount,
  historicalAdjustmentCount,
  customItemCount,  // ✅ Changed from tempRowCount
  isSaving,
  onEditModeChange,
  onNameChange,
  onDescriptionChange,
  onSave,
  onCancel,
  formatRelativeTime,
}) => {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">
          {currentScenario?.scenario_name || 'Untitled Scenario'}
          {hasUnsavedChanges && (
            <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
              Unsaved changes
            </span>
          )}
        </h3>
        {!editMode && currentScenario && (
          <button
            onClick={() => onEditModeChange(true)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            ✏️ Edit
          </button>
        )}
      </div>

      {!editMode ? (
        // ===== VIEW MODE =====
        <>
          {/* Description */}
          {currentScenario?.scenario_description && (
            <p className="text-gray-700 mb-4">
              {currentScenario.scenario_description}
            </p>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">Method</div>
              <div className="text-sm font-medium">
                {averagingMethod.toUpperCase()}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">Adjustments</div>
              <div className="text-sm font-medium">
                {adjustmentCount} active
                {historicalAdjustmentCount > 0 && (
                  <span className="text-gray-400 text-xs ml-1">
                    ({historicalAdjustmentCount} historical)
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">Custom Items</div>
              <div className="text-sm font-medium">{customItemCount}</div>
            </div>
          </div>

          {/* Metadata */}
          {currentScenario && (
            <div className="pt-4 border-t border-gray-200">
              <div className="text-xs text-gray-500 space-y-1">
                <div>Last saved: {formatRelativeTime(currentScenario.updated_at)}</div>
                <div>Created by: {currentScenario.created_by}</div>
              </div>
            </div>
          )}
        </>
      ) : (
        // ===== EDIT MODE =====
        <>
          <div className="space-y-4">
            {/* Name Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scenario Name *
              </label>
              <input
                type="text"
                value={editedName}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="e.g., Q1 Conservative Growth"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Description Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description (optional)
              </label>
              <textarea
                value={editedDescription}
                onChange={(e) => onDescriptionChange(e.target.value)}
                placeholder="Describe assumptions and changes..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Summary Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-sm text-blue-900 font-medium mb-2">
                This scenario includes:
              </div>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Forecast Method: {averagingMethod.toUpperCase()}</li>
                <li>• {adjustmentCount} manual adjustments</li>
                <li>• {customItemCount} custom line items</li>  {/* ✅ Changed from tempRowCount */}
              </ul>
            </div>
          </div>

          {/* Edit Mode Buttons */}
          <div className="flex items-center gap-3 mt-6">
            <button
              onClick={onSave}
              disabled={!editedName.trim() || isSaving}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>💾 Save Changes</>
              )}
            </button>
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ScenarioInfoCard;